/**
 * Cambios locales sobre el payload de `GET agent/prompt-library/agency/`, para que la pantalla
 * responda al instante después de guardar, desactivar, restaurar o adaptar. Cada mutación exitosa
 * va seguida de una recarga silenciosa que trae los campos que sólo arma ese endpoint (autor, uso
 * por la IA, versión): estos helpers sólo tienen que dejar la lista coherente mientras tanto.
 *
 * Funciones puras: devuelven un payload nuevo y nunca tocan el que reciben.
 */

// La forma de éxito de las vistas de la biblioteca: responden el payload pelado, sin `status`
// (contrato §1.3). Un error llega del `APIClient` con `http_status`, o como `NETWORK_ERROR`.
export function isLibraryPayload(response) {
    return Boolean(response) && Array.isArray(response.own);
}

export function isPromptPayload(response) {
    return Boolean(response) && response.id !== undefined && response.id !== null && !response.http_status;
}

// `DELETE`, `used/`, `rate/` y `portal-visibility/` contestan `{status: 'ok'}` en minúscula.
export function isAck(response) {
    return Boolean(response) && response.status === 'ok';
}

/**
 * Arma una fila de la lista propia a partir del `library_data()` que devuelven `create/`, `PUT`,
 * `fork/` y `restore/`, que no traen los campos de la pantalla de agencia. Los toma de la fila que
 * ya había (al editar o restaurar) o de lo que se sabe al crear (el cliente elegido).
 */
export function toOwnRow(data, {previous = null, client = null} = {}) {
    return {
        client: previous ? previous.client : client,
        created_by: previous ? previous.created_by : null,
        created_at: previous ? previous.created_at : null,
        updated_at: previous ? previous.updated_at : null,
        version_number: previous ? previous.version_number : 1,
        last_mcp_use: previous ? previous.last_mcp_use : null,
        ...data,
    };
}

function withoutId(list, id) {
    return (list || []).filter((prompt) => prompt.id !== id);
}

/** Agrega la fila o la reemplaza si ya estaba. */
export function upsertOwn(library, row) {
    const own = library.own || [];
    const exists = own.some((prompt) => prompt.id === row.id);
    return {
        ...library,
        own: exists ? own.map((prompt) => (prompt.id === row.id ? row : prompt)) : [...own, row],
    };
}

export function patchOwn(library, id, patch) {
    return {
        ...library,
        own: (library.own || []).map((prompt) => (prompt.id === id ? {...prompt, ...patch} : prompt)),
    };
}

/** Marca un prompt de Dashbo como adaptado por la copia `forkId`. */
export function markAdapted(library, baseId, forkId) {
    return {
        ...library,
        dashbo: (library.dashbo || []).map((prompt) => (
            prompt.id === baseId ? {...prompt, adapted: {id: forkId, scope: 'AGENCY'}} : prompt
        )),
    };
}

/**
 * La tarjeta de Dashbo cuya copia era `forkId` vuelve a ofrecer «Adaptar». El Backend sólo marca
 * `adapted` mientras la copia está activa: sin esto, desactivar la copia dejaba la tarjeta en
 * «Adaptado · ver en De {agencia}» apuntando a una fila que ya no está.
 */
export function unmarkAdapted(library, forkId) {
    return {
        ...library,
        dashbo: (library.dashbo || []).map((prompt) => (
            prompt.adapted && prompt.adapted.id === forkId ? {...prompt, adapted: null} : prompt
        )),
    };
}

// Una copia de toda la agencia de un prompt de Dashbo: es la que marca su tarjeta como adaptada.
function isAgencyForkOf(row, baseId) {
    return !row.client && Boolean(row.source) && row.source.id === baseId;
}

/**
 * Sale de la lista y pasa a «Desactivados». Sólo lo puede desactivar quien lo creó, que es
 * exactamente quien lo puede restaurar, así que siempre entra ahí.
 */
export function deactivateOwn(library, id) {
    const row = (library.own || []).find((prompt) => prompt.id === id);
    if (!row) return library;
    return unmarkAdapted({
        ...library,
        own: withoutId(library.own, id),
        inactive_own: [row, ...withoutId(library.inactive_own, id)],
    }, id);
}

export function restoreOwn(library, id, data = null) {
    const row = (library.inactive_own || []).find((prompt) => prompt.id === id)
        || (library.own || []).find((prompt) => prompt.id === id);
    if (!row) return library;
    const restored = data ? toOwnRow(data, {previous: row}) : row;
    const next = upsertOwn({...library, inactive_own: withoutId(library.inactive_own, id)}, restored);
    const base = (next.dashbo || []).find((prompt) => isAgencyForkOf(restored, prompt.id) && !prompt.adapted);
    return base ? markAdapted(next, base.id, restored.id) : next;
}

export function patchDashboSocial(library, id, social) {
    return {
        ...library,
        dashbo: (library.dashbo || []).map((prompt) => (prompt.id === id ? {...prompt, social} : prompt)),
    };
}

/**
 * El voto: el mismo valor otra vez lo borra. Devuelve el `social` nuevo (optimista) y el valor que
 * viaja al backend.
 */
export function nextRating(social, clicked) {
    const base = social || {uses: 0, thumbs_up: 0, thumbs_down: 0, my_rating: null};
    const current = base.my_rating;
    const next = current === clicked ? null : clicked;
    return {
        value: next,
        social: {
            ...base,
            thumbs_up: base.thumbs_up - (current === 'UP' ? 1 : 0) + (next === 'UP' ? 1 : 0),
            thumbs_down: base.thumbs_down - (current === 'DOWN' ? 1 : 0) + (next === 'DOWN' ? 1 : 0),
            my_rating: next,
        },
    };
}
